import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type User } from '@prisma/client'
import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Link,
	redirect,
	useFetcher,
	useLoaderData,
	useNavigate,
	useOutletContext,
} from '@remix-run/react'
import {
	CoinsIcon,
	CrosshairIcon,
	GiftIcon,
	InfoIcon,
	TreeDeciduous,
	XIcon,
} from 'lucide-react'
import posthog from 'posthog-js'
import { useEffect } from 'react'
import { Button } from '#app/components/ui/button'
import { Card } from '#app/components/ui/card'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip'
import { getUserById } from '#app/models/user/get-user'
import { Interval, PlanId } from '#app/services/stripe/plans'
import { useStoreContext } from '#app/store'
import { authenticator } from '#app/utils/auth.server'
import { isEarthDay } from '#app/utils/is-earth-day.js'
import { cn } from '#app/utils/misc'
import { groupPlots } from '#app/utils/plots'
import { type LayoutContextType } from './_layout'

type LoaderData = {
	user: User | null
}

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/basket' }],
}

export async function loader({ request }: LoaderFunctionArgs) {
	const session = await authenticator.isAuthenticated(request)

	if (!session) return json<LoaderData>({ user: null })

	const user = await getUserById(session.id)

	if (user && !user.name) {
		return redirect('/profile/edit?onboarding=true&redirectTo=/basket')
	}

	return json<LoaderData>({ user })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => [
	{ title: `Your basket | Erda` },
	{
		name: 'description',
		content: `Your selection of Plots. Each owned Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		name: 'keywords',
		content: 'basket, selected, buy, purchase, checkout',
	},
	{ name: 'og:title', content: `Your basket | Erda` },
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/basket.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Your selection of Plots. Each owned Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/basket.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{ name: 'twitter:title', content: `Your basket | Erda` },
	{
		name: 'twitter:description',
		content: `Your selection of Plots. Each owned Plot helps plant two trees in developing communities, combating poverty and environmental harm.`,
	},
]

export default function Basket() {
	const loaderData = useLoaderData() as LoaderData

	const selectedPlotIds = useStoreContext(s => s.selectedPlotIds)
	const setSelectedPlotIds = useStoreContext(s => s.setSelectedPlotIds)
	const highlightedPlotIds = useStoreContext(s => s.highlightedPlotIds)
	const setHighlightedPlotIds = useStoreContext(s => s.setHighlightedPlotIds)
	const setHighlightedPlotGroupIds = useStoreContext(
		s => s.setHighlightedPlotGroupIds,
	)

	const navigate = useNavigate()

	const fetcher = useFetcher()
	const isLoading = fetcher.state !== 'idle'

	const planId = PlanId.PLOT_OWNERSHIP
	const planInterval = Interval.YEAR

	const { flyToPlot } = useOutletContext<LayoutContextType>()

	const costPerPlot = 2
	const treesPerPlot = 2

	const totalCost = (selectedPlotIds.length * costPerPlot).toFixed(2)
	// const totalCharityDonation = (selectedPlotIds.length / 2).toFixed(2)
	const ownText = `Own ${selectedPlotIds.length > 1 ? 'these Plots' : 'this Plot'}`
	const giftText = `Gift ${selectedPlotIds.length > 1 ? 'these Plots' : 'this Plot'}`

	useEffect(() => {
		if (selectedPlotIds.length === 0) {
			setHighlightedPlotIds([])
			setHighlightedPlotGroupIds([])
			navigate('/')
		}
	}, [
		navigate,
		selectedPlotIds,
		setHighlightedPlotGroupIds,
		setHighlightedPlotIds,
	])

	const { groupedPlots, singlePlots } = groupPlots(selectedPlotIds)

	return (
		<div className="flex min-h-full flex-col justify-between">
			<h1 className="sr-only">Basket</h1>
			<div className="flex flex-col gap-3 pb-4">
				{/* TODO (low) some content to explain what can be done with plots. Why buy one. Charity info */}
				{/* Link to FAQs? or some way to drop them in here more concisely */}
				<div className="self-end">
					<Button
						variant="link"
						onClick={() => {
							setSelectedPlotIds([])
							posthog.capture('clear_selected_plots')
						}}
					>
						Clear selection
					</Button>
				</div>
				<div className="flex flex-col gap-2 pb-3">
					{groupedPlots.map((groupPlot, index) => (
						<Card
							key={groupPlot[0]}
							className={cn(
								'flex flex-col gap-2 p-2 px-3',
								highlightedPlotIds?.includes(groupPlot[0]) &&
									'border-orange-200 bg-orange-50 dark:bg-amber-800',
							)}
						>
							<div className="flex items-center justify-between gap-2">
								<div className="flex items-center gap-2">
									<span className="text-sm">
										Plot group{groupedPlots.length > 1 && ` ${index + 1}`}
									</span>
									<span
										className="flex items-center gap-1 text-xs text-muted-foreground"
										data-vaul-no-drag
									>
										{groupPlot.length} plots{' '}
										<TooltipProvider>
											<Tooltip data-vaul-no-drag>
												<TooltipTrigger>
													<InfoIcon
														className="h-3 w-3 text-muted-foreground"
														data-vaul-no-drag
													/>
												</TooltipTrigger>
												<TooltipContent data-vaul-no-drag>
													<div
														className="flex max-w-xs flex-col gap-1"
														data-vaul-no-drag
													>
														<p className="text-xs">
															Neighbouring plots are automatically combined into
															one larger area, sharing the same content and
															handle.
														</p>
														<p className="text-xs">
															You can choose to separate them later.
														</p>
													</div>
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									</span>
								</div>
								<Button
									data-vaul-no-drag
									variant="link"
									size="icon"
									className="h-5 w-5 text-muted-foreground transition-colors hover:text-accent-foreground "
									onClick={() => {
										setSelectedPlotIds(
											selectedPlotIds.filter(id => !groupPlot.includes(id)),
										)
										posthog.capture('clear_selected_plot', {
											id: groupPlot[0],
										})
									}}
								>
									<XIcon className="h-4 w-4" />
								</Button>
							</div>
							<div className="flex gap-2">
								<Button
									data-vaul-no-drag
									variant="outline"
									size="sm"
									className="h-5 items-center px-1.5 text-xs"
									onClick={() => {
										flyToPlot(groupPlot[0], 'collapse')
										setHighlightedPlotIds(groupPlot)
										posthog.capture('selected_plot_locate', {
											id: groupPlot[0],
										})
									}}
								>
									<CrosshairIcon className="h-3.5 w-3.5" /> Locate
								</Button>
							</div>
						</Card>
					))}
					{singlePlots.map((plotId, index) => (
						<Card
							key={plotId}
							className={cn(
								'flex flex-col gap-2 p-2 px-3',
								highlightedPlotIds?.includes(plotId) &&
									'border-orange-200 bg-orange-50 dark:bg-amber-800',
							)}
						>
							<div className="flex items-center justify-between gap-2">
								<div className="flex items-center gap-2">
									<span className="text-sm">
										Plot{singlePlots.length > 1 && ` ${index + 1}`}
									</span>
									{/* <span
                    className="flex items-center gap-1 text-xs text-muted-foreground"
                    data-vaul-no-drag>
                    {plotId}
                  </span> */}
								</div>
								{/* TODO (low) show location details? */}
								<Button
									data-vaul-no-drag
									variant="link"
									size="icon"
									className="h-5 w-5 text-muted-foreground transition-colors hover:text-accent-foreground"
									onClick={() => {
										setSelectedPlotIds(
											selectedPlotIds.filter(id => id !== plotId),
										)
										posthog.capture('clear_selected_plot', {
											id: plotId,
										})
									}}
								>
									<XIcon className="h-4 w-4" />
								</Button>
							</div>
							<div className="flex gap-2">
								<Button
									data-vaul-no-drag
									variant="outline"
									size="sm"
									className="h-5 items-center px-1.5 text-xs"
									onClick={() => {
										flyToPlot(plotId, 'collapse')
										setHighlightedPlotIds([plotId])
										posthog.capture('selected_plot_locate', {
											id: plotId,
										})
									}}
								>
									<CrosshairIcon className="h-3.5 w-3.5" /> Locate
								</Button>
							</div>
						</Card>
					))}
				</div>
			</div>

			<div className="sticky -bottom-10 flex w-full flex-col gap-2 border-t bg-background pb-10 pt-3">
				<p className="flex items-center justify-center gap-2 text-center text-sm">
					<CoinsIcon /> ${totalCost}, renews yearly
				</p>
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger>
							<div className="-mt-1 flex items-center justify-center gap-2 text-center text-sm">
								<TreeDeciduous />{' '}
								<span>
									Plants{' '}
									{isEarthDay() ? (
										<>
											<span className="line-through">
												{selectedPlotIds.length * treesPerPlot}
											</span>{' '}
											{selectedPlotIds.length * 3}
										</>
									) : (
										selectedPlotIds.length * treesPerPlot
									)}{' '}
									trees, and {selectedPlotIds.length * treesPerPlot} trees on
									renewal
								</span>
								<span className="flex items-center gap-1">
									<InfoIcon className="h-3 w-3" />
									<span className="text-xs">How?</span>
								</span>
							</div>
						</TooltipTrigger>
						<TooltipContent>
							With each ownership and renewal, we donate to Trees for the Future
							(
							<a
								href="https://trees.org"
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 underline"
							>
								https://trees.org
							</a>
							) to plant two trees in developing communities. This donation
							helps break the cycle of hunger, poverty, and environmental
							destruction.
						</TooltipContent>{' '}
					</Tooltip>
				</TooltipProvider>

				{loaderData.user ? (
					// TODO (med) check if the plots are still available (somone else may have bought them in the meantime)
					<fetcher.Form
						action="/resources/stripe/create-checkout"
						method="post"
						className="w-full"
					>
						<div className="flex flex-col gap-2">
							<Button
								data-vaul-no-drag
								name="plan"
								value={JSON.stringify({
									planId,
									planInterval,
									selectedPlots: selectedPlotIds,
								})}
								type="submit"
								className="relative w-full"
								onClick={() => {
									posthog.capture('checkout_initiated', {
										selectedPlots: selectedPlotIds,
									})
								}}
							>
								{/* {!isLoading && <ArrowRightIcon className="h-5 w-5" />} */}
								<span>{isLoading ? 'Loading...' : ownText}</span>
							</Button>
							<Button
								variant="outline"
								data-vaul-no-drag
								type="button"
								className="relative w-full"
								onClick={() => {
									posthog.capture('gift_plots_clicked', {
										selectedPlots: selectedPlotIds,
									})
								}}
								asChild
							>
								<Link to="/gift-basket">
									<GiftIcon className="h-5 w-5" />
									<span>{giftText}</span>
								</Link>
							</Button>
						</div>
						<div className="mt-2 flex justify-center">
							<p className="text-xs text-muted-foreground">
								By continuing, you agree to our{' '}
								<Button variant="link" asChild className="text-xs">
									<Link to="/terms?from=/basket">Terms</Link>
								</Button>{' '}
								and{' '}
								<Button variant="link" asChild className="text-xs">
									<Link to="/privacy?from=/basket">Privacy Policy</Link>
								</Button>
								.
							</p>
						</div>
					</fetcher.Form>
				) : (
					<div className="flex flex-col gap-2">
						<Button
							data-vaul-no-drag
							asChild
							className="w-full"
							onClick={() => {
								posthog.capture('checkout_redirect_login', {
									selectedPlots: selectedPlotIds,
								})
							}}
						>
							<Link to="/login?redirectTo=/basket">
								{/* <ArrowRightIcon className="h-5 w-5" /> */}
								<span>{ownText}</span>
							</Link>
						</Button>
						<Button
							variant="outline"
							data-vaul-no-drag
							className="relative w-full"
							onClick={() => {
								posthog.capture('gift_plots_clicked', {
									selectedPlots: selectedPlotIds,
								})
							}}
							asChild
						>
							<Link to="/gift-basket">
								<GiftIcon className="h-5 w-5" />
								<span>{giftText}</span>
							</Link>
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
